import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { TrendingDown, TrendingUp, AlertCircle, Target } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, ComposedChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Progress } from '../ui/progress';

const monthlyNRWData = [
  { month: 'Jan', total: 12.5, physical: 7.5, commercial: 3.0, metering: 2.0, target: 10 },
  { month: 'Feb', total: 11.8, physical: 7.0, commercial: 2.8, metering: 2.0, target: 10 },
  { month: 'Mar', total: 13.2, physical: 8.2, commercial: 3.0, metering: 2.0, target: 10 },
  { month: 'Apr', total: 12.0, physical: 7.2, commercial: 2.8, metering: 2.0, target: 10 },
  { month: 'May', total: 11.5, physical: 6.8, commercial: 2.7, metering: 2.0, target: 10 },
  { month: 'Jun', total: 11.5, physical: 6.5, commercial: 3.0, metering: 2.0, target: 10 },
];

const villageNRWData = [
  { name: 'Cortalim', current: 8.5, lastMonth: 9.2, target: 10 },
  { name: 'Verna', current: 12.3, lastMonth: 11.8, target: 10 },
  { name: 'Margao', current: 15.7, lastMonth: 16.2, target: 10 },
  { name: 'Benaulim', current: 9.2, lastMonth: 10.1, target: 10 },
  { name: 'Colva', current: 11.8, lastMonth: 12.5, target: 10 },
];

const lossTypeData = [
  { category: 'Pipe Leaks', volume: 420, cost: 126000 },
  { category: 'Storage Overflow', volume: 180, cost: 54000 },
  { category: 'Illegal Connections', volume: 150, cost: 45000 },
  { category: 'Meter Inaccuracy', volume: 120, cost: 36000 },
  { category: 'Unauthorized Use', volume: 80, cost: 24000 },
];

export const NRWAnalysis = ({ selectedVillage }) => {
  return (
    <div className="space-y-6">
      {/* Page Title */}
      <div>
        <h1 className="text-3xl font-bold text-foreground mb-2">Non-Revenue Water (NRW) Analysis</h1>
        <p className="text-muted-foreground">Track water losses and identify improvement opportunities</p>
      </div>
      
      {/* Key NRW Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-sm text-muted-foreground mb-2">Current NRW %</p>
                <p className="text-3xl font-bold text-destructive">11.5%</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-destructive/10 flex items-center justify-center">
                <TrendingDown className="w-6 h-6 text-destructive" />
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <TrendingDown className="w-3 h-3 text-success" />
              <span className="text-success font-medium">-0.5% from last month</span>
            </div>
          </CardContent>
        </Card>
        
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-sm text-muted-foreground mb-2">NRW Volume</p>
                <p className="text-3xl font-bold">900 m³</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <TrendingDown className="w-6 h-6 text-primary" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Per day average</p>
          </CardContent>
        </Card>
        
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-sm text-muted-foreground mb-2">Revenue Loss</p>
                <p className="text-3xl font-bold text-destructive">₹285K</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-destructive/10 flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-destructive" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Monthly estimated loss</p>
          </CardContent>
        </Card>
        
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-sm text-muted-foreground mb-2">Target NRW</p>
                <p className="text-3xl font-bold text-success">10%</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-success/10 flex items-center justify-center">
                <Target className="w-6 h-6 text-success" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Progress value={85} className="flex-1 h-2" indicatorClassName="bg-warning" />
              <span className="text-xs font-medium">85%</span>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* NRW Trend Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>6-Month NRW Trend by Component</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart data={monthlyNRWData}>
                <defs>
                  <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--destructive))" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="hsl(var(--destructive))" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" />
                <YAxis stroke="hsl(var(--muted-foreground))" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }} 
                />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="total" 
                  stroke="hsl(var(--destructive))" 
                  fill="url(#colorTotal)" 
                  name="Total NRW %" 
                />
                <Line 
                  type="monotone" 
                  dataKey="target" 
                  stroke="hsl(var(--success))" 
                  strokeDasharray="5 5" 
                  name="Target" 
                  dot={false}
                />
                <Bar dataKey="physical" fill="hsl(var(--chart-5))" name="Physical Loss" stackId="a" />
                <Bar dataKey="commercial" fill="hsl(var(--chart-4))" name="Commercial Loss" stackId="a" />
                <Bar dataKey="metering" fill="hsl(var(--chart-3))" name="Metering Error" stackId="a" />
              </ComposedChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>Village-wise NRW Comparison</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={villageNRWData} layout="horizontal">
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis type="number" stroke="hsl(var(--muted-foreground))" />
                <YAxis dataKey="name" type="category" stroke="hsl(var(--muted-foreground))" width={100} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }} 
                />
                <Legend />
                <Bar dataKey="current" fill="hsl(var(--chart-1))" name="Current Month %" />
                <Bar dataKey="lastMonth" fill="hsl(var(--chart-2))" name="Last Month %" />
                <Bar dataKey="target" fill="hsl(var(--success))" name="Target %" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
      
      {/* Loss Type Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Loss Type Analysis</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-4">By Volume (m³/day)</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={lossTypeData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis 
                    dataKey="category" 
                    stroke="hsl(var(--muted-foreground))" 
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    fontSize={12}
                  />
                  <YAxis stroke="hsl(var(--muted-foreground))" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }} 
                  />
                  <Bar dataKey="volume" fill="hsl(var(--chart-1))" name="Volume (m³)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-4">Estimated Cost Impact (₹/month)</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={lossTypeData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis 
                    dataKey="category" 
                    stroke="hsl(var(--muted-foreground))" 
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    fontSize={12}
                  />
                  <YAxis stroke="hsl(var(--muted-foreground))" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }} 
                    formatter={(value) => `₹${(value ?? 0).toLocaleString()}`}
                  />
                  <Bar dataKey="cost" fill="hsl(var(--destructive))" name="Cost (₹)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Action Items */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Priority Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex gap-4 p-4 bg-destructive/5 rounded-lg border border-destructive/20">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-destructive flex items-center justify-center text-destructive-foreground font-bold">
                  1
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-foreground mb-1">Repair Pipe Leaks in Margao</h4>
                  <p className="text-sm text-muted-foreground mb-2">15.7% NRW - highest in Salcette Block</p>
                  <div className="flex items-center gap-2">
                    <Badge variant="destructive">High Priority</Badge>
                    <span className="text-xs text-muted-foreground">Est. savings: ₹126K/month</span>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-4 p-4 bg-warning/5 rounded-lg border border-warning/20">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-warning flex items-center justify-center text-warning-foreground font-bold">
                  2
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-foreground mb-1">Address Storage Overflow</h4>
                  <p className="text-sm text-muted-foreground mb-2">OHT-2 & OHT-5 overflow detection</p>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-warning/10 text-warning border-warning/20">Medium</Badge>
                    <span className="text-xs text-muted-foreground">Est. savings: ₹54K/month</span>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-4 p-4 bg-muted rounded-lg border border-border">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold">
                  3
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-foreground mb-1">Upgrade Flow Meters</h4>
                  <p className="text-sm text-muted-foreground mb-2">Replace outdated meters in DMAs 8-12</p>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">Normal</Badge>
                    <span className="text-xs text-muted-foreground">Est. accuracy gain: 2%</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>NRW Reduction Roadmap</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Q1 2024 Target</span>
                  <span className="text-sm font-bold">10.5%</span>
                </div>
                <Progress value={75} className="h-2 mb-1" indicatorClassName="bg-warning" />
                <p className="text-xs text-muted-foreground">Current: 11.5% | Gap: 1.0%</p>
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Q2 2024 Target</span>
                  <span className="text-sm font-bold">10.0%</span>
                </div>
                <Progress value={50} className="h-2 mb-1" />
                <p className="text-xs text-muted-foreground">Planned actions: Leak repair, meter upgrade</p>
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Year End Target</span>
                  <span className="text-sm font-bold">9.0%</span>
                </div>
                <Progress value={30} className="h-2 mb-1" indicatorClassName="bg-success" />
                <p className="text-xs text-muted-foreground">Full infrastructure optimization</p>
              </div>
              
              <div className="mt-6 p-4 bg-success/5 rounded-lg border border-success/20">
                <h4 className="text-sm font-semibold text-success mb-2">Projected Annual Savings</h4>
                <p className="text-2xl font-bold text-foreground">₹3.42 Crore</p>
                <p className="text-xs text-muted-foreground mt-1">Based on 2% NRW reduction</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NRWAnalysis;